import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import instance from '../apiInstance';
import './CompleteProfileModal.css';
import CompleteProfileFormActions from './CompleteProfileFormActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faGift, faCreditCard } from '@fortawesome/free-solid-svg-icons';
import {
  displayFieldLabel as promoDisplayLabel,
  emptyAnswer,
  fieldOptions,
  isEmptyAnswer as isPromoEmpty,
  isMultiSelect,
  isSurveyComplete as isPromoComplete,
  toggleOption,
  normalizePromoSurveyFields,
  PROMO_OUTRO_NAME,
  PROMO_OUTRO_PLACEHOLDER,
} from './promoSurveyFields';
import {
  displayFieldLabel as compraDisplayLabel,
  isEmptyAnswer as isCompraEmpty,
  isPromoCompraSurvey,
  isSurveyComplete as isCompraComplete,
  normalizePromoCompraSurveyFields,
} from './promoCompraSurveyFields';

/** Timer pós-promo teto → form dia + pagamento (persiste em qualquer página). */
export const PROMO_COMPRA_DELAY_MS = 20_000;
export const PROMO_COMPRA_DUE_KEY = 'promo_compra_due';
export const PROMO_COMPRA_ARMED_AT_KEY = 'promo_compra_armed_at';

const PROMO_ALL_DONE_KEY = 'promo_surveys_all_done';

function getDueStorage() {
  try {
    return window.localStorage;
  } catch {
    try {
      return window.sessionStorage;
    } catch {
      return null;
    }
  }
}

function readDueFlag() {
  const store = getDueStorage();
  return store?.getItem(PROMO_COMPRA_DUE_KEY) === 'true';
}

function markDue() {
  const store = getDueStorage();
  if (store) store.setItem(PROMO_COMPRA_DUE_KEY, 'true');
}

function clearDue() {
  const store = getDueStorage();
  if (store) store.removeItem(PROMO_COMPRA_DUE_KEY);
  try {
    window.sessionStorage.removeItem(PROMO_COMPRA_ARMED_AT_KEY);
  } catch {
    /* ignore */
  }
}

function readArmedAt() {
  try {
    const raw = window.sessionStorage.getItem(PROMO_COMPRA_ARMED_AT_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeArmedAt(ts = Date.now()) {
  try {
    window.sessionStorage.setItem(PROMO_COMPRA_ARMED_AT_KEY, String(ts));
  } catch {
    /* ignore */
  }
}

function prepareSurvey(data) {
  const title = String(data?.title || "").toLowerCase();
  const isCompra = isPromoCompraSurvey(data)
    || title.includes("quando e como")
    || (title.includes("prefere") && title.includes("compra") && !title.includes("experi"));
  const fields = isCompra
    ? normalizePromoCompraSurveyFields()
    : normalizePromoSurveyFields(data.fields);
  const initialAnswers = {};
  fields.forEach((f) => {
    initialAnswers[f.name] = isCompra ? '' : emptyAnswer(f);
  });
  return { ...data, fields, isCompra, initialAnswers };
}

/** Promo fase 1 (teto) na 5ª foto · fase 2 (dia/pagamento) ~20s depois, em qualquer tela. */
const PromoSurveyModalLab = () => {
  const { token } = useSelector((state) => state.auth);
  const location = useLocation();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const cadastroBlockingRef = useRef(false);
  const compraTimerRef = useRef(null);
  const fetchingRef = useRef(false);

  const openFromApi = useCallback((data) => {
    const prepared = prepareSurvey(data);
    setSurvey(prepared);
    setAnswers(prepared.initialAnswers);
    setError('');
    window.dispatchEvent(new Event('promo_survey_open'));
  }, []);

  const fetchPromoPending = useCallback(({ allowBudget = true, allowCompra = false } = {}) => {
    if (sessionStorage.getItem(PROMO_ALL_DONE_KEY)) return Promise.resolve(null);
    if (fetchingRef.current) return Promise.resolve(null);

    fetchingRef.current = true;
    return instance.get('/surveys/pending', { params: { context: 'promo' } })
      .then((res) => {
        if (!res.data) {
          sessionStorage.setItem(PROMO_ALL_DONE_KEY, 'true');
          clearDue();
          return null;
        }
        const isCompra = isPromoCompraSurvey(res.data);
        if (isCompra && !allowCompra) return null;
        if (!isCompra && !allowBudget) return null;
        openFromApi(res.data);
        return res.data;
      })
      .finally(() => {
        fetchingRef.current = false;
      });
  }, [openFromApi]);

  const armCompraTimer = useCallback(() => {
    writeArmedAt(Date.now());
    clearDue();
  }, []);

  const openCompraIfDue = useCallback(() => {
    if (!readDueFlag()) return;
    fetchPromoPending({ allowBudget: false, allowCompra: true }).catch((err) => {
      console.error('Error fetching promo compra survey:', err);
    });
  }, [fetchPromoPending]);

  const scheduleCompraTimer = useCallback((remainingMs) => {
    if (compraTimerRef.current) clearTimeout(compraTimerRef.current);
    if (remainingMs <= 0) {
      markDue();
      openCompraIfDue();
      return;
    }
    compraTimerRef.current = setTimeout(() => {
      markDue();
      openCompraIfDue();
    }, remainingMs);
  }, [openCompraIfDue]);

  /** Quem já respondeu teto antes do deploy: arma timer na 1ª visita. */
  const bootstrapCompraIfNeeded = useCallback(() => {
    if (readArmedAt() || readDueFlag()) return;
    instance.get('/surveys/pending', { params: { context: 'promo' } })
      .then((res) => {
        if (res.data && isPromoCompraSurvey(res.data)) {
          writeArmedAt(Date.now());
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) return undefined;

    const handleTrigger = () => {
      if (cadastroBlockingRef.current) return;
      fetchPromoPending({
        allowBudget: true,
        allowCompra: readDueFlag(),
      }).catch((err) => console.error('Error fetching promo survey:', err));
    };

    const onCadastroOpen = () => { cadastroBlockingRef.current = true; };
    const onCadastroClose = () => {
      cadastroBlockingRef.current = false;
      handleTrigger();
    };

    bootstrapCompraIfNeeded();
    window.addEventListener('trigger_promo_survey', handleTrigger);
    window.addEventListener('cadastro_modal_open', onCadastroOpen);
    window.addEventListener('cadastro_modal_closed', onCadastroClose);
    window.addEventListener('cadastro_completed', onCadastroClose);
    return () => {
      window.removeEventListener('trigger_promo_survey', handleTrigger);
      window.removeEventListener('cadastro_modal_open', onCadastroOpen);
      window.removeEventListener('cadastro_modal_closed', onCadastroClose);
      window.removeEventListener('cadastro_completed', onCadastroClose);
    };
  }, [token, fetchPromoPending, bootstrapCompraIfNeeded]);

  // Timer global: ~20s após teto → due → abre dia/pagamento em qualquer página
  useEffect(() => {
    if (!token || survey) return undefined;

    if (readDueFlag()) {
      openCompraIfDue();
      return undefined;
    }

    const armedAt = readArmedAt();
    if (!armedAt) return undefined;

    const remaining = PROMO_COMPRA_DELAY_MS - (Date.now() - armedAt);
    scheduleCompraTimer(remaining);
    return () => {
      if (compraTimerRef.current) clearTimeout(compraTimerRef.current);
    };
  }, [token, survey, location.pathname, openCompraIfDue, scheduleCompraTimer]);

  const handleChange = (name, value) => {
    setAnswers((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const closeSurvey = () => {
    setSurvey(null);
    window.dispatchEvent(new Event('promo_survey_closed'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!survey) return;

    const isCompra = survey.isCompra;
    const labelFn = isCompra ? compraDisplayLabel : promoDisplayLabel;
    const emptyFn = isCompra ? isCompraEmpty : isPromoEmpty;

    for (const field of survey.fields) {
      if (field.required && emptyFn(field, answers[field.name])) {
        setError(`Por favor, preencha: ${labelFn(field)}`);
        return;
      }
    }

    setLoading(true);
    try {
      const payloadAnswers = survey.isCompra
        ? Object.fromEntries(
          survey.fields.map((f) => [f.name, answers[f.name]]).filter(([, v]) => v != null && v !== ''),
        )
        : answers;
      const res = await instance.post(`/surveys/${survey.id}/submit`, { answers: payloadAnswers });
      if (res.data?.points_awarded > 0) {
        alert(`Parabéns! Você ganhou ${res.data.points_awarded} pontos!`);
      }

      if (survey.isCompra) {
        sessionStorage.setItem(PROMO_ALL_DONE_KEY, 'true');
        sessionStorage.setItem('promo_survey_completed', 'true');
        clearDue();
        closeSurvey();
      } else {
        armCompraTimer();
        closeSurvey();
      }
    } catch (err) {
      setError('Erro ao enviar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!survey) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const blockEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockEscape, true);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', blockEscape, true);
    };
  }, [survey]);

  if (!survey) return null;

  const isCompra = survey.isCompra;
  const labelFn = isCompra ? compraDisplayLabel : promoDisplayLabel;
  const canSubmit = (
    isCompra ? isCompraComplete(survey.fields, answers) : isPromoComplete(survey.fields, answers)
  ) && !loading;

  return (
    <div className="complete-profile-overlay">
      <div className="complete-profile-modal">
        <div className="complete-profile-header">
          <FontAwesomeIcon
            icon={isCompra ? faCreditCard : faBullhorn}
            size="3x"
            color="#D4AF37"
            style={{ marginBottom: 16 }}
          />
          <h2>{survey.title}</h2>
          {survey.description && <p>{survey.description}</p>}

          {survey.reward_points > 0 && (
            <div className="complete-profile-points-badge">
              <FontAwesomeIcon icon={faGift} style={{ marginRight: 6 }} />
              Ganhe {survey.reward_points} Pontos
            </div>
          )}
        </div>

        <form className="complete-profile-form" onSubmit={handleSubmit}>
          <div className="complete-profile-form-body">
          {survey.fields.map((field, idx) => (
            <div key={idx} className="complete-profile-input-group">
              <label>{labelFn(field)} {field.required && '*'}</label>
              {isMultiSelect(field) && (
                <span className="complete-profile-field-hint">Pode escolher mais de um</span>
              )}

              {field.type === 'text' && !isMultiSelect(field) && (
                <input
                  type="text"
                  value={answers[field.name] || ''}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  required={field.required}
                  placeholder={field.name === PROMO_OUTRO_NAME ? PROMO_OUTRO_PLACEHOLDER : undefined}
                />
              )}

              {field.type === 'select' && (
                <select
                  className="complete-profile-select"
                  value={answers[field.name] || ''}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  required={field.required}
                >
                  <option value="">Selecione uma opção</option>
                  {field.options?.map((opt, i) => (
                    <option key={i} value={opt}>{opt}</option>
                  ))}
                </select>
              )}

              {(isMultiSelect(field) || field.type === 'checkbox') && (
                <div className="complete-profile-radio-group">
                  {fieldOptions(field).map((opt, i) => {
                    const selected = Array.isArray(answers[field.name]) && answers[field.name].includes(opt);
                    return (
                      <div className="complete-profile-radio-option" key={`${field.name}-${opt}`}>
                        <input
                          type="checkbox"
                          name={field.name}
                          id={`promo-lab-${field.name}-${i}`}
                          value={opt}
                          checked={selected}
                          onChange={() => handleChange(field.name, toggleOption(answers[field.name], opt))}
                        />
                        <label className="complete-profile-radio-label" htmlFor={`promo-lab-${field.name}-${i}`}>
                          {opt}
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}

              {(field.type === 'radio' || field.name === 'promo_budget') && !isMultiSelect(field) && (
                <div className="complete-profile-radio-group">
                  {field.options?.map((opt, i) => (
                    <div className="complete-profile-radio-option" key={i}>
                      <input
                        type="radio"
                        name={field.name}
                        id={`promo-lab-${field.name}-${i}`}
                        value={opt}
                        checked={answers[field.name] === opt}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        required={field.required && !answers[field.name]}
                      />
                      <label className="complete-profile-radio-label" htmlFor={`promo-lab-${field.name}-${i}`}>
                        {opt}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          </div>
          <CompleteProfileFormActions
            error={error}
            hint="Flor, preencha para continuar"
            showHint={!canSubmit}
            loading={loading}
            canSubmit={canSubmit}
            submitLabel={isCompra ? 'Continuar' : (survey.reward_points > 0 ? 'Enviar e Ganhar Pontos' : 'Enviar')}
          />
        </form>
      </div>
    </div>
  );
};

export default PromoSurveyModalLab;
